import { useMemo } from "react";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../../components/SearchableSelect";

interface EntidadeExecutanteFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Nomes sugeridos: sub-empreiteiros e fornecedores, já deduplicados e ordenados. */
  options: string[];
  /** Estilo do campo de texto livre; o seletor com pesquisa traz o seu próprio. */
  inputClassName: string;
  /** Placeholder do campo de texto livre. */
  placeholder?: string;
  maxLength?: number;
  /** "fixed" quando o campo vive numa tabela dentro de `overflow-x-auto`. */
  panelPosition?: "absolute" | "fixed";
  /** Nome acessível do seletor. Sem um destes, é uma combobox sem nome. */
  ariaLabel?: string;
}

const TEXTO_LIVRE = "";

/**
 * Escolher quem executa: um seletor com pesquisa sobre as entidades registadas, e escrita à mão
 * quando a pessoa não está em nenhuma das tabelas — a primeira opção significa "escrevo eu".
 *
 * O texto livre não é opcional: a coluna é uma string e há linhas antigas com nomes que não existem
 * em nenhuma das duas tabelas. Um seletor puro perdia-as.
 */
export function EntidadeExecutanteField({
  value,
  onChange,
  options,
  inputClassName,
  placeholder = "Nome de quem executa",
  maxLength,
  panelPosition,
  ariaLabel = "Quem executa",
}: EntidadeExecutanteFieldProps) {
  // Um valor que não conste das entidades registadas é, por definição, texto livre — e é assim que
  // as linhas antigas continuam a mostrar o que têm gravado em vez de aparecerem vazias.
  const selecionado = useMemo(
    () => (options.includes(value) ? value : TEXTO_LIVRE),
    [options, value],
  );

  const opcoes = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: TEXTO_LIVRE, label: "— Escrever à mão —" },
      ...options.map((nome) => ({ value: nome, label: nome })),
    ],
    [options],
  );

  return (
    <div className="flex flex-col gap-1">
      <SearchableSelect<string>
        ariaLabel={ariaLabel}
        value={selecionado}
        onChange={(next) => {
          const escolhido = next ?? TEXTO_LIVRE;
          // Voltar a "escrever à mão" quando já se estava a escrever à mão não é
          // apagar: quem abre a lista só para ver se a entidade está registada não
          // pode perder o nome que já lá tinha escrito.
          if (escolhido === TEXTO_LIVRE) {
            onChange(options.includes(value) ? TEXTO_LIVRE : value);
            return;
          }
          onChange(escolhido);
        }}
        options={opcoes}
        allowClear={false}
        panelPosition={panelPosition}
        searchPlaceholder="Procurar entidade…"
        style={{ minWidth: "12rem" }}
      />

      {selecionado === TEXTO_LIVRE && (
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className={inputClassName}
          placeholder={placeholder}
          maxLength={maxLength}
        />
      )}
    </div>
  );
}
